import React from "react";
import { FlashbarProps, Input, Select } from "@amzn/awsui-components-react-v3";

export const COLUMN_DEFINITIONS = [
  {
    id: "logicalGroupName",
    header: "Logical Group",
    cell: (item: any) => item.logicalGroupName,
    width: 220
  },
  {
    id: "ownership",
    header: "Ownership",
    cell: (e: any) => e.ownership,
    width: 120,
  },
  {
    id: "supplierId",
    header: "Supplier Code",
    cell: (item: any) => item.supplierId,
    width: 140,
  },
  {
    id: "manufacturer",
    header: "Manufacturer",
    cell: (e: any) => e.manufacturer,
    width: 150,
  },
  {
    id: "moq",
    header: "MOQ (optional)",
    cell: (e: any) => e.moq,
    width: 170,
    editConfig: {
      ariaLabel: "Name",
      editIconAriaLabel: "editable",
      errorIconAriaLabel: "Name Error",
      editingCell: (item, { currentValue, setValue }) => {
        return (
          <Input
            autoFocus={true}
            value={currentValue ?? item.moq}
            onChange={(event) => setValue(event.detail.value)}
          />
        );
      },
      disabledReason: (item) => {
        if (item.level === "logicalGroup") {
          return "You cannot set MOQ at LG level.";
        }
        return undefined;
      },
      validation: (item, value) => {
        if (value && !/^\d+(\.\d+)?$/.test(value.toString())) {
          return "MOQ must be a positive number.";
        }
      },
    },
  },
  {
    id: "tam",
    header: "% TAM",
    cell: (e: any) => e.tam === null ? e.tamDisplay : e.tam,
    width: 130,
    editConfig: {
      ariaLabel: "Name",
      editIconAriaLabel: "editable",
      errorIconAriaLabel: "Name Error",
      editingCell: (item, { currentValue, setValue }) => {
        return (
          <Input
            autoFocus={true}
            value={currentValue ?? item.tam}
            onChange={(event) => setValue(event.detail.value)}
          />
        );
      },
      disabledReason: (item) => {
        if (item.ownership === "BUYSELL") {
          return "You cannot set TAM% when ownership is BUYSELL.";
        } else if (item.level === "ipn") {
          return "You cannot set TAM% at IPN level.";
        }
        return undefined;
      },
      validation: (item, value) => {
        if (value && !/^\d+(\.\d+)?$/.test(value.toString())) {
          return "TAM% must be a positive number.";
        }
      },
    },
  },
  {
    id: "stackRackIpn",
    header: "Stack Rank IPN",
    cell: (e: any) => e.stackRackIpn === null ? e.stackRackIpnDisplay : StackRankOptions[e.stackRackIpn],
    width: 180,
    editConfig: {
      ariaLabel: "stackRackIpn",
      editIconAriaLabel: "editable",
      editingCell: (item, { currentValue, setValue }) => {
        const value = currentValue ?? item.stackRackIpn;
        const options = getStackRankIpnOptions(item.ownership);
        return (
          <Select
            autoFocus={true}
            expandToViewport={true}
            selectedOption={options.find((option) => option.value === value) ?? null}
            onChange={(event) => {
              setValue(event.detail.selectedOption.value ?? item.stackRackIpn);
            }}
            options={options}
          />
        );
      },
      disabledReason: (item) => {
        if (item.level === "ipn") {
          return "You cannot set Stack Rack IPN at IPN level.";
        }
        return undefined;
      },
    },
  },
  {
    id: "custom",
    header: "Custom %",
    cell: (e: any) => e.custom === null && e.ipnLevelStackRackIpn === "CUSTOM" ? e.customDisplay : e.custom,
    width: 120,
    editConfig: {
      ariaLabel: "Custom",
      editIconAriaLabel: "editable",
      errorIconAriaLabel: "Name Error",
      editingCell: (item, { currentValue, setValue }) => {
        return (
          <Input
            autoFocus={true}
            value={currentValue ?? item.custom}
            onChange={(event) => setValue(event.detail.value)}
          />
        );
      },
      disabledReason: (item) => {
        if (item.ipnLevelStackRackIpn !== "CUSTOM") {
          return "You cannot set Custom% unless Stack Rank IPN is set to Custom.";
        } 
        return undefined;
      },
      validation: (item, value) => {
        if (value && !/^\d+(\.\d+)?$/.test(value.toString())) {
          return "Custom% must be a positive number.";
        }
      },
    },
  },
];

export const getStackRankIpnOptions = (ownership) => {
  const oemStratOptions = [
    { label: "OEM First Age Based", value: "OEM_FIRST_AGE_BASED" },
    { label: "OEM First Generation Based", value: "OEM_FIRST_GENERATION_BASED" },
    { label: "Strat First Age Based", value: "STRAT_FIRST_AGE_BASED" },
    { label: "Strat First Generation Based", value: "STRAT_FIRST_GENERATION_BASED" },
    { label: "Custom", value: "CUSTOM" },
  ];

  if (ownership === "OEM+STRATBUY") {
    return oemStratOptions;
  } else {
    return [
      { label: "Age Based", value: "AGE_BASED" },
      { label: "Generation Based", value: "GENERATION_BASED" },
      { label: "Custom", value: "CUSTOM" },
    ];
  }
};

export enum StackRankOptions {
  CUSTOM = "Custom",
  AGE_BASED = "Age Based",
  GENERATION_BASED = "Generation Based",
  OEM_FIRST_AGE_BASED = "OEM First Age Based",
  OEM_FIRST_GENERATION_BASED = "OEM First Generation Based",
  STRAT_FIRST_AGE_BASED = "Strat First Age Based",
  STRAT_FIRST_GENERATION_BASED = "Strat First Generation Based",
}

export enum SAVE_EDITS_STATE {
  SUCCESS = "SAVE_EDITS_SUCCESS",
  STACK_RANK_ERROR = "SAVE_EDITS_STACK_RANK_ERROR",
  TAM_PERCENTAGE_MISMATCH_ERROR = "TAM_PERCENTAGE_MISMATCH_ERROR",
  CUSTOM_PERCENTAGE_MISMATCH_ERROR = "CUSTOM_PERCENTAGE_MISMATCH_ERROR",
  TAM_PERCENTAGE_NOT_SET_ERROR = "TAM_PERCENTAGE_NOT_SET_ERROR",
  TAM_DATE_NOT_SET_ERROR = "TAM_DATE_NOT_SET_ERROR",
  API_FAILURE_ERROR = "API_FAILURE_ERROR"
}

export const flashbarDetails = (): FlashbarProps.MessageDefinition[] => {
  return [
    {
      header: "Your consumption preference edits have been saved successfully.",
      content: "The inventory data will update automatically within a few hours. You will receive an email notification once the updates are complete.",
      type: "success",
      dismissible: true,
      dismissLabel: "Dismiss message",
      id: SAVE_EDITS_STATE.SUCCESS,
    },
    {
      header: "Error: Stack Rank IPN Preference Missing",
      content: "Unable to save superset edits. A stack rank IPN preference for each logical group is required.",
      type: "error",
      dismissible: true,
      dismissLabel: "Dismiss message",
      id: SAVE_EDITS_STATE.STACK_RANK_ERROR,
    },
    {
      header: "Error: Percentage Mismatch for Superset group",
      content: "We could not save your superset edits because the total TAM% for each group does not equal 100%. Please adjust the percentages in logical groups.",
      type: "error",
      dismissible: true,
      dismissLabel: "Dismiss message",
      id: SAVE_EDITS_STATE.TAM_PERCENTAGE_MISMATCH_ERROR,
    },
    {
      header: "Error: Percentage Mismatch for IPN group",
      content: "We could not save your superset edits because the total Custom% for each group does not equal 100%. Please adjust the percentages in IPN groups.",
      type: "error",
      dismissible: true,
      dismissLabel: "Dismiss message",
      id: SAVE_EDITS_STATE.CUSTOM_PERCENTAGE_MISMATCH_ERROR,
    },
    {
      header: "Error: Percentage not set for Superset group",
      content: "We could not save your superset edits because TAM% is not set for all logical groups. Please set the percentages in logical groups.",
      type: "error",
      dismissible: true,
      dismissLabel: "Dismiss message",
      id: SAVE_EDITS_STATE.TAM_PERCENTAGE_NOT_SET_ERROR,
    },
    {
      header: "Error: TAM start and end date not set",
      content: "We could not save your superset edits because TAM start and end date is not set. Please use Date edits to set the date.",
      type: "error",
      dismissible: true,
      dismissLabel: "Dismiss message",
      id: SAVE_EDITS_STATE.TAM_DATE_NOT_SET_ERROR,
    },
    {
      content: "There was an error in the system to complete the request. Please refresh the page and try again.",
      type: "error",
      dismissible: true,
      dismissLabel: "Dismiss message",
      id: SAVE_EDITS_STATE.API_FAILURE_ERROR,
    },
  ];
};